'use client'

import { useEffect, useRef } from 'react'
import type { UseFormReturn } from 'react-hook-form'

import type { UnifiedOrderFormValues } from '@/features/order/order-form/lib/unified-order-schema'

/** Shared with use-unified-order-form.ts's resetFormAfterOrder, so a successful submit
 * can clear the exact same key this hook writes to. */
export function orderFormValuesStorageKey(cardId: string) {
  return `order-form-values-${cardId}`
}

/**
 * Persists order form values (price, TWAP params, per-row amounts/denominations) to localStorage —
 * this is what lets a failed submission's in-progress values (including TWAP size) survive an
 * accidental reload so the user doesn't have to retype them to retry.
 *
 * Restore strategy:
 *   Waits until form items are first populated (phase 2, after parent effects run),
 *   so that symbol-change-reset in useUnifiedOrderForm fires BEFORE we restore.
 *
 * Save: debounced 500ms on any form value change.
 *   Saves non-items fields, itemAmounts (symbol→amount), and itemAmountUnits (symbol→amountUnit).
 *
 * Cleanup: that "survive a reload" case is the only reason to keep this around at all, so it's
 * cleared as soon as it's no longer needed — on a successful submit (use-unified-order-form.ts's
 * resetFormAfterOrder) and on unmount here, which covers the order form's sheet/popup closing or
 * its dashboard widget being removed (an in-SPA unmount runs this cleanup; an actual tab close/
 * hard reload doesn't, which is exactly the crash/reload case this is meant to survive).
 */
export function useOrderFormValuesPersist(
  cardId: string,
  form: UseFormReturn<UnifiedOrderFormValues>
) {
  const storageKey = orderFormValuesStorageKey(cardId)
  const restoredRef = useRef(false)

  useEffect(() => {
    return () => {
      localStorage.removeItem(storageKey)
    }
  }, [storageKey])

  // Restore ALL saved values once items are first populated.
  // Running here (phase 2 child effect) ensures parent effects like symbol-change-reset
  // have already fired, so our restored globalAmount/price won't be overwritten.
  const items = form.watch('items')
  useEffect(() => {
    if (restoredRef.current || items.length === 0) return
    restoredRef.current = true

    const saved = localStorage.getItem(storageKey)
    if (!saved) return
    try {
      const parsed = JSON.parse(saved) as Partial<UnifiedOrderFormValues> & {
        itemAmounts?: Record<string, string>
        itemAmountUnits?: Record<string, string>
      }
      const {
        items: _oldItems,
        itemAmounts: _itemAmounts,
        itemAmountUnits: _itemAmountUnits,
        price: _price,
        priceAnchor: _priceAnchor,
        priceOffsetUnit: _priceOffsetUnit,
        ...nonItems
      } = parsed

      // Restore non-items fields (TWAP params, etc.) — price and anchor excluded
      if (Object.keys(nonItems).length > 0) {
        form.reset({ ...form.getValues(), ...nonItems })
      }

      // Per-row sizes (`itemAmounts`/`itemAmountUnits`) are saved but not restored: a reload
      // starts the ticket fresh (rows seeded from the link group's focused tab, see
      // useOrderFormLinkedSeed), and an old size landing on a re-seeded row of the same symbol
      // would resurrect the previous session's order.
    } catch {
      // Ignore malformed storage
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items])

  // Save on change (debounced 500ms) — non-items fields + itemAmounts map
  // NOTE: form.watch() returns a new reference on every render (including ticker re-renders).
  // To prevent the timeout from being cancelled on every ticker update, we compare serialized
  // values and only reset the timer when form content actually changed.
  const values = form.watch()
  const saveKeyRef = useRef<string | null>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const { items: currentItems, price: _price, ...nonItems } = values
    const itemAmounts: Record<string, string> = {}
    const itemAmountUnits: Record<string, string> = {}
    currentItems.forEach((item) => {
      if (item.symbol && item.amount) itemAmounts[item.symbol] = item.amount
      if (item.symbol && item.amountUnit) itemAmountUnits[item.symbol] = item.amountUnit
    })
    const serialized = JSON.stringify({ ...nonItems, itemAmounts, itemAmountUnits })

    // Skip if form content hasn't actually changed (avoids cancelling timeout on ticker re-renders)
    if (saveKeyRef.current === serialized) return
    saveKeyRef.current = serialized

    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(() => {
      localStorage.setItem(storageKey, serialized)
    }, 500)
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [values, storageKey])
}
